<style>
@import "@/assets/css/custom.css";
</style>

<template>
	<div>
		<Breadcrumbs main="Accounts" title="Sales" />

		<div class="container-fluid">
			<div class="card">
				<div class="card-body">
					<form @submit.prevent="submit">
						<div class="row">
							<div :class="form.user_type === '4' ? 'col-md-3' : 'col-md-4'" class="form-group mt-2">
								<label class="control-label">Category<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.category" @change="onCategoryChange">
									<option value="">— Select —</option>
									<option value="1">Product</option>
									<option value="2">Raw material</option>
									<option value="3">Other Services</option>
								</select>
								<div v-if="errors.category" class="text-danger small mt-1">{{ errors.category }}</div>
							</div>

							<div
								v-if="form.category === '1'"
								:class="form.user_type === '4' ? 'col-md-3' : 'col-md-4'"
								class="form-group mt-2"
							>
								<label class="control-label">Product type<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.product_type" @change="onProductTypeChange">
									<option value="">— Select —</option>
									<option v-for="p in allProducts" :key="'pt-' + p.id" :value="String(p.id)">
										{{ p.product_name }}
									</option>
								</select>
								<div v-if="errors.product_type" class="text-danger small mt-1">{{ errors.product_type }}</div>
							</div>

							<div :class="form.user_type === '4' ? 'col-md-3' : 'col-md-4'" class="form-group mt-2">
								<label class="control-label">User type<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.user_type" @change="onUserTypeChange">
									<option value="">— Select —</option>
									<option value="1">Dealer</option>
									<option value="2">Customer</option>
									<option v-if="form.category !== '3'" value="4">Cash Sale (Other party)</option>
								</select>
								<div v-if="errors.user_type" class="text-danger small mt-1">{{ errors.user_type }}</div>
							</div>

							<div v-if="form.user_type === '4'" class="col-md-3 form-group mt-2">
								<label class="control-label">Bank account<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.bank_account_id">
									<option value="">— Select bank —</option>
									<option v-for="b in bankAccounts" :key="b.id" :value="String(b.id)">
										{{ b.bank_name }} — {{ b.account_number }}
									</option>
								</select>
								<div v-if="errors.bank_account_id" class="text-danger small mt-1">{{ errors.bank_account_id }}</div>
							</div>
						</div>

						<!-- Dealer — existing -->
						<div class="row" v-if="form.user_type === '1' && !isNewDealer">
							<div class="col-md-4 form-group mt-2 position-relative">
								<label class="control-label">Dealer<span class="text-danger">*</span></label>
								<input
									type="text"
									class="form-control"
									v-model="dealerSearch"
									placeholder="Search dealer…"
									@focus="showDealerSuggestions = true"
									@input="showDealerSuggestions = true"
									@blur="hideDealerSuggestions"
								/>
								<div v-if="errors.dealer_id" class="text-danger small mt-1">{{ errors.dealer_id }}</div>

								<div v-if="showDealerSuggestions" class="list-group position-absolute w-100" style="z-index: 10; max-height: 220px; overflow: auto">
									<button
										v-for="d in filteredDealers"
										:key="'d-' + d.id"
										type="button"
										class="list-group-item list-group-item-action"
										@click="selectDealer(d)"
									>
										{{ d.first_name }}
									</button>
									<div v-if="!filteredDealers.length" class="list-group-item text-muted">No matches</div>
								</div>
								<small v-if="selectedDealer" class="text-muted d-block mt-1">Selected: {{ selectedDealer.first_name }}</small>
								<div class="mt-2">
									<a href="#" class="small" @click.prevent="enableNewDealer">+ Add new dealer</a>
								</div>
							</div>
						</div>

						<!-- Dealer — new -->
						<div class="row" v-if="form.user_type === '1' && isNewDealer">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Dealer name<span class="text-danger">*</span></label>
								<input class="form-control" type="text" v-model="form.dealer_name" placeholder="Enter dealer name" />
								<div v-if="errors.dealer_name" class="text-danger small mt-1">{{ errors.dealer_name }}</div>
								<div class="mt-2">
									<a href="#" class="small" @click.prevent="selectExistingDealer">← Select existing dealer</a>
								</div>
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Dealer mobile<span class="text-danger">*</span></label>
								<input class="form-control" v-model="form.dealer_mobile" v-mobile-limit placeholder="Enter dealer mobile" maxlength="10" minlength="10" />
								<div v-if="errors.dealer_mobile" class="text-danger small mt-1">{{ errors.dealer_mobile }}</div>
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Password<span class="text-danger">*</span></label>
								<input class="form-control" type="text" v-model="form.password" disabled />
								<div v-if="errors.password" class="text-danger small mt-1">{{ errors.password }}</div>
							</div>
							<div class="col-md-8 form-group mt-2">
								<label class="control-label">Address<span class="text-danger">*</span></label>
								<textarea
									class="form-control"
									rows="2"
									v-model="form.address"
									placeholder="Enter address"
									@input="syncDealerAddressToShipping"
								></textarea>
								<div v-if="errors.address" class="text-danger small mt-1">{{ errors.address }}</div>
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">State<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.state_id" @change="onDealerStateChange">
									<option value="">— Select state —</option>
									<option v-for="s in states" :key="'st-' + s.id" :value="String(s.id)">{{ s.name }}</option>
								</select>
								<div v-if="errors.state_id" class="text-danger small mt-1">{{ errors.state_id }}</div>
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">District<span class="text-danger">*</span></label>
								<select class="form-select" v-model="form.district_id" :disabled="!form.state_id || districtsLoading">
									<option value="">— Select district —</option>
									<option v-for="d in districts" :key="'dist-' + d.id" :value="String(d.id)">{{ d.name }}</option>
								</select>
								<div v-if="districtsLoading" class="text-muted small mt-1">Loading districts…</div>
								<div v-if="errors.district_id" class="text-danger small mt-1">{{ errors.district_id }}</div>
							</div>
						</div>

						<!-- Customer — existing -->
						<div class="row" v-if="form.user_type === '2' && !isNewCustomer">
							<div class="col-md-6 form-group mt-2 position-relative">
								<label class="control-label">Customer<span class="text-danger">*</span></label>
								<input
									type="text"
									class="form-control"
									v-model="customerSearch"
									placeholder="Search customer by company / mobile…"
									@focus="showCustomerSuggestions = true"
									@input="showCustomerSuggestions = true"
									@blur="hideCustomerSuggestions"
								/>
								<div v-if="errors.customer_id" class="text-danger small mt-1">{{ errors.customer_id }}</div>

								<div v-if="showCustomerSuggestions" class="list-group position-absolute w-100" style="z-index: 10; max-height: 220px; overflow: auto">
									<button
										v-for="c in filteredCustomers"
										:key="'c-' + c.id"
										type="button"
										class="list-group-item list-group-item-action"
										@click="selectCustomer(c)"
									>
										<div class="d-flex justify-content-between">
											<span>{{ c.company_name }}</span>
											<small class="text-muted">{{ c.mobile || "—" }}</small>
										</div>
									</button>
									<div v-if="!filteredCustomers.length" class="list-group-item text-muted">No matches</div>
								</div>
								<small v-if="selectedCustomer" class="text-muted d-block mt-1">
									Selected: {{ selectedCustomer.company_name }} {{ selectedCustomer.mobile ? '(' + selectedCustomer.mobile + ')' : '' }}
								</small>
								<div class="mt-2">
									<a href="#" class="small" @click.prevent="enableNewCustomer">+ Add new customer</a>
								</div>
							</div>
						</div>

						<!-- Customer — new -->
						<div class="row" v-if="form.user_type === '2' && isNewCustomer">
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Customer name<span class="text-danger">*</span></label>
								<input class="form-control" type="text" v-model="form.user_name" placeholder="Enter customer name" />
								<div v-if="errors.user_name" class="text-danger small mt-1">{{ errors.user_name }}</div>
							</div>
							<div class="col-md-4 form-group mt-2">
								<label class="control-label">Customer mobile<span class="text-danger">*</span></label>
								<input class="form-control" v-model="form.user_mobile" v-mobile-limit placeholder="Enter customer mobile" maxlength="10" minlength="10" />
								<div v-if="errors.user_mobile" class="text-danger small mt-1">{{ errors.user_mobile }}</div>
							</div>
							<div class="col-md-4 form-group mt-2">	
								<label class="control-label">Password<span class="text-danger">*</span></label>
								<input class="form-control" type="text" v-model="form.password" disabled />
								<div v-if="errors.password" class="text-danger small mt-1">{{ errors.password }}</div>
							</div>
							<div class="col-md-8">
								<a href="#" class="small" @click.prevent="selectExistingCustomer">← Select existing customer</a>
							</div>
						</div>

						<!-- Shipping address (dealer / customer) -->
						<div class="row" v-if="showShippingAddress">
							<div class="col-md-8 form-group mt-2">
								<label class="control-label">Shipping address<span class="text-danger">*</span></label>
								<textarea
									class="form-control"
									rows="3"
									v-model="form.ship_to_address"
									placeholder="Enter shipping address"
								></textarea>
								<small class="text-muted">Pre-filled from the selected profile; edit if delivery is elsewhere.</small>
								<div v-if="errors.ship_to_address" class="text-danger small mt-1">{{ errors.ship_to_address }}</div>
							</div>
						</div>

						<!-- Other party (cash sale) -->
						<div class="row" v-if="form.user_type === '4'">
							<div class="col-md-6 form-group mt-2">
								<label class="control-label">Customer name<span class="text-danger">*</span></label>
								<input class="form-control" type="text" v-model="form.user_name" placeholder="Customer name" />
								<div v-if="errors.user_name" class="text-danger small mt-1">{{ errors.user_name }}</div>
							</div>
							<div class="col-md-6 form-group mt-2">
								<label class="control-label">Customer mobile<span class="text-danger">*</span></label>
								<input class="form-control" v-model="form.user_mobile" v-mobile-limit placeholder="Customer mobile" maxlength="10" minlength="10" />
								<div v-if="errors.user_mobile" class="text-danger small mt-1">{{ errors.user_mobile }}</div>
							</div>
						</div>

						<!-- Product details -->
						<div v-if="form.category === '1' && form.product_type" class="mt-4">
							<h6 class="border-bottom pb-2">{{ productTypeLabel(form.product_type) }} details</h6>
							<div class="row">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Quantity<span class="text-danger">*</span></label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="1"
										v-model="form.quantity"
										placeholder="0"
									/>
									<div v-if="errors.quantity" class="text-danger small mt-1">{{ errors.quantity }}</div>
								</div>

								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Invoice number</label>
									<input class="form-control" type="text" v-model="form.invoice_number" placeholder="Optional" />
									<div v-if="errors.invoice_number" class="text-danger small mt-1">{{ errors.invoice_number }}</div>
								</div>

								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Invoice date</label>
									<input class="form-control" type="date" v-model="form.invoice_date" />
									<div v-if="errors.invoice_date" class="text-danger small mt-1">{{ errors.invoice_date }}</div>
								</div>
							</div>
						</div>

						<!-- Raw materials -->
						<div v-if="form.category === '2'" class="mt-4">
							<div class="d-flex justify-content-between align-items-center border-bottom pb-2">
								<h6 class="mb-0">Raw materials</h6>
								<button type="button" class="btn btn-sm btn-primary" @click="addMaterialRow">Add material</button>
							</div>

							<div class="table-responsive mt-3">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th style="min-width: 280px">Material</th>
											<th style="min-width: 120px">Quantity</th>
											<th style="min-width: 160px">Amount (₹)</th>
											<th style="width: 80px">Remove</th>
										</tr>
									</thead>
									<tbody v-if="materialRows.length">
										<tr v-for="(row, idx) in materialRows" :key="'smr-' + idx" align="center">
											<td class="text-start">
												<input
													type="text"
													class="form-control form-control-sm"
													v-model="row.material_search"
													placeholder="Search material…"
													@focus="openMaterialDropdown(idx, $event)"
													@input="openMaterialDropdown(idx, $event)"
													@blur="scheduleCloseMaterialDropdown"
												/>
												<small v-if="row.material_id" class="text-muted d-block mt-1">
													Selected: {{ materialLabelById(row.material_id) }}
												</small>
											</td>
											<td>
												<input
													class="form-control form-control-sm"
													type="number"
													min="1"
													step="1"
													v-model="row.qty"
													@input="syncMaterialTotals"
													placeholder="0"
												/>
											</td>
											<td>
												<input
													class="form-control form-control-sm"
													type="number"
													min="0"
													step="0.01"
													v-model="row.amount"
													@input="syncMaterialTotals"
													placeholder="0.00"
												/>
												<small class="text-muted d-block mt-1">Total for this qty</small>
											</td>
											<td>
												<button type="button" class="btn btn-sm btn-danger" @click="removeMaterialRow(idx)">X</button>
											</td>
										</tr>
									</tbody>
									<tbody v-else>
										<tr>
											<td colspan="4" class="text-center text-muted py-3">Add at least one material</td>
										</tr>
									</tbody>
								</table>
							</div>

							<div v-if="errors.raw_material_ids" class="text-danger small">{{ errors.raw_material_ids }}</div>
							<div v-if="errors.raw_material_qty" class="text-danger small">{{ errors.raw_material_qty }}</div>
							<div v-if="errors.raw_material_price" class="text-danger small">{{ errors.raw_material_price }}</div>

							<div class="row mt-2">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Invoice number</label>
									<input class="form-control" type="text" v-model="form.invoice_number" placeholder="Optional" />
								</div>
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Invoice date</label>
									<input class="form-control" type="date" v-model="form.invoice_date" />
								</div>
							</div>
						</div>

						<!-- Other Services -->
						<div v-if="isOtherServices" class="mt-4">
							<h6 class="border-bottom pb-2">Service charges</h6>
							<div class="row">
								<div class="col-md-3 form-group mt-2">
									<label class="control-label">Fitment charge (₹)</label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										v-model="form.fitment_charge"
										placeholder="0.00"
										@input="syncOtherServiceTotals"
									/>
									<div v-if="errors.fitment_charge" class="text-danger small mt-1">{{ errors.fitment_charge }}</div>
								</div>
								<div class="col-md-3 form-group mt-2">
									<label class="control-label">Fuel Charge (₹)</label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										v-model="form.fuel_charge"
										placeholder="0.00"
										@input="syncOtherServiceTotals"
									/>
									<div v-if="errors.fuel_charge" class="text-danger small mt-1">{{ errors.fuel_charge }}</div>
								</div>
								<div class="col-md-3 form-group mt-2">
									<label class="control-label">Other Charges (₹)</label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										v-model="form.other_charges"
										placeholder="0.00"
										@input="syncOtherServiceTotals"
									/>
									<div v-if="errors.other_charges" class="text-danger small mt-1">{{ errors.other_charges }}</div>
								</div>
								<div class="col-md-3 form-group mt-2">
									<label class="control-label">Total Amount (₹)</label>
									<input class="form-control" type="number" :value="form.total_amount" readonly placeholder="0.00" />
									<small class="text-muted">Sum of the three charges. Booked as pending due.</small>
									<div v-if="errors.total_amount" class="text-danger small mt-1">{{ errors.total_amount }}</div>
								</div>
							</div>
						</div>

						<!-- Amount block -->
						<div v-if="showSaleAmountBlock" class="mt-4">
							<h6 class="border-bottom pb-2">Amount</h6>
							<div class="row">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Total Amount (₹)<span class="text-danger">*</span></label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										v-model="form.total_amount"
										:readonly="form.category === '2'"
										placeholder="0.00"
									/>
									<small v-if="form.category === '2'" class="text-muted">Auto-calculated from material amounts.</small>
									<div v-if="errors.total_amount" class="text-danger small mt-1">{{ errors.total_amount }}</div>
								</div>
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">GST</label>
									<select class="form-select" v-model="form.gst_amount">
										<option value="0">— No GST —</option>
										<option value="18">18% GST</option>
									</select>
									<div v-if="errors.gst_amount" class="text-danger small mt-1">{{ errors.gst_amount }}</div>
								</div>
							</div>
							<small class="text-muted d-block mt-1">Total amount is inclusive of GST.</small>
						</div>

						<!-- Dealer / Customer payment (aligned with recharge flow) -->
						<div
							v-if="isDealerOrCustomer && showSaleAmountBlock && (form.user_type === '1' ? (selectedDealer || isNewDealer) : (selectedCustomer || isNewCustomer))"
							class="mt-4"
						>
							<h6 class="border-bottom pb-2">Payment</h6>
							<div class="row">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Payment status<span class="text-danger">*</span></label>
									<select
										class="form-select"
										v-model="form.payment_status"
										@change="onPaymentStatusChange"
									>
										<option value="">— Select —</option>
										<option value="1">Received</option>
										<option value="2">Not received</option>
										<option value="4">Advance</option>
									</select>
									<div v-if="errors.payment_status" class="text-danger small mt-1">{{ errors.payment_status }}</div>
								</div>

								<div class="col-md-4 form-group mt-2" v-if="form.payment_status === '4'">
									<label class="control-label">Amount from advance<span class="text-danger">*</span></label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										v-model="form.amount_from_credit"
										placeholder="0.00"
									/>
									<div v-if="userCreditLoading" class="mt-1"><small class="text-muted">Loading advance balance…</small></div>
									<div v-else-if="userCredit && userCredit.credit_available_amount != null" class="mt-1">
										<small class="text-success">Advance available: ₹{{ userCredit.credit_available_amount }}</small>
									</div>
									<div v-if="userCreditError" class="text-danger small mt-1">{{ userCreditError }}</div>
									<div v-if="errors.amount_from_credit" class="text-danger small mt-1">{{ errors.amount_from_credit }}</div>
								</div>
							</div>

							<div class="row" v-if="form.payment_status === '1'">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Payment type<span class="text-danger">*</span></label>
									<select class="form-select" v-model="form.payment_type" @change="onPaymentTypeChange">
										<option value="">— Select —</option>
										<option value="1">Cash</option>
										<option value="2">Online</option>
										<option value="3">Both</option>
									</select>
									<div v-if="errors.payment_type" class="text-danger small mt-1">{{ errors.payment_type }}</div>
								</div>
							</div>

							<div class="row" v-if="form.payment_status === '1' && (form.payment_type === '2' || form.payment_type === '3')">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Bank account<span class="text-danger">*</span></label>
									<select class="form-select" v-model="form.bank_account_id">
										<option value="">— Select bank —</option>
										<option v-for="b in bankAccounts" :key="b.id" :value="String(b.id)">
											{{ b.bank_name }} — {{ b.account_number }}
										</option>
									</select>
									<small class="text-muted">Required for online and both payments.</small>
									<div v-if="errors.bank_account_id" class="text-danger small mt-1">{{ errors.bank_account_id }}</div>
								</div>
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Online amount (₹)<span class="text-danger">*</span></label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										v-model="form.cash_online"
										placeholder="0.00"
									/>
									<div v-if="errors.cash_online" class="text-danger small mt-1">{{ errors.cash_online }}</div>
								</div>
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Payment / transaction ID</label>
									<input class="form-control" type="text" v-model="form.payment_id" placeholder="UTR / reference" />
									<div v-if="errors.payment_id" class="text-danger small mt-1">{{ errors.payment_id }}</div>
								</div>
							</div>

							<div class="row" v-if="form.payment_status === '1' && (form.payment_type === '1' || form.payment_type === '3')">
								<div class="col-md-4 form-group mt-2">
									<label class="control-label">Cash Amount (₹)<span class="text-danger">*</span></label>
									<input
										class="form-control"
										type="number"
										min="0"
										step="0.01"
										v-model="form.cash_in_hand"
										placeholder="0.00"
									/>
									<div v-if="errors.cash_in_hand" class="text-danger small mt-1">{{ errors.cash_in_hand }}</div>
								</div>
							</div>

							<div class="row" v-if="dealerCustomerPaymentBreakdown">
								<div class="col-md-12">
									<div class="card border-light mt-2">
										<div class="card-body py-2">
											<div class="row text-center small">
												<div class="col-md-3">
													<span class="text-muted d-block">Sale total</span>
													<span class="fw-bold">₹{{ dealerCustomerPaymentBreakdown.total }}</span>
												</div>
												<div class="col-md-3" v-if="form.payment_type === '2' || form.payment_type === '3'">
													<span class="text-muted d-block">Online</span>
													<span class="fw-bold">₹{{ dealerCustomerPaymentBreakdown.online }}</span>
												</div>
												<div class="col-md-3" v-if="form.payment_type === '1' || form.payment_type === '3'">
													<span class="text-muted d-block">Cash</span>
													<span class="fw-bold">₹{{ dealerCustomerPaymentBreakdown.cash }}</span>
												</div>
												<div class="col-md-3">
													<span class="text-muted d-block">Match</span>
													<span class="fw-bold" :class="dealerCustomerPaymentBreakdown.statusClass">{{
														dealerCustomerPaymentBreakdown.statusLabel
													}}</span>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>

						<div class="row">
							<div v-if="showPaymentDate" class="col-md-4 form-group mt-2">
								<label class="control-label">Payment date<span v-if="isPaymentDateRequired" class="text-danger">*</span></label>
								<input class="form-control" type="date" v-model="form.payment_date" />
								<div v-if="errors.payment_date" class="text-danger small mt-1">{{ errors.payment_date }}</div>
							</div>

							<div v-if="showStandalonePaymentId" class="col-md-4 form-group mt-2">
								<label class="control-label">Payment / transaction ID</label>
								<input class="form-control" type="text" v-model="form.payment_id" placeholder="UTR / reference" />
								<div v-if="errors.payment_id" class="text-danger small mt-1">{{ errors.payment_id }}</div>
							</div>
						</div>

						<div class="row">
							<div class="col-md-12 form-group mt-3">
								<label class="control-label">Remarks<span v-if="isOtherServices" class="text-danger">*</span></label>
								<textarea
									class="form-control"
									rows="2"
									v-model="form.remarks"
									:placeholder="isOtherServices ? 'Enter remarks' : 'Optional remarks'"
								></textarea>
								<div v-if="errors.remarks" class="text-danger small mt-1">{{ errors.remarks }}</div>
							</div>
						</div>

						<div class="row mt-4">
							<div class="col-12">
								<button type="submit" class="btn btn-primary" :disabled="submitting">
									<span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
									{{ submitting ? "Saving..." : "Save sale" }}
								</button>
								<button type="button" class="btn btn-light ms-2" :disabled="submitting" @click="resetAll">Reset</button>
							</div>
						</div>

						<!-- Material dropdown portal -->
						<div
							v-if="materialDropdown.open && materialRows[materialDropdown.idx]"
							ref="materialDropdownEl"
							class="list-group"
							:style="materialDropdownStyle"
							@mousedown.prevent
						>
							<button
								v-for="m in filteredMaterials(materialRows[materialDropdown.idx].material_search)"
								:key="'smp-' + m.id"
								type="button"
								class="list-group-item list-group-item-action"
								@mousedown.prevent
								@click="selectMaterial(materialDropdown.idx, m); closeMaterialDropdown()"
							>
								<div class="fw-bold">{{ m.raw_material_name }}</div>
								<small class="text-muted">{{ m.description }}</small>
							</button>
							<div v-if="!filteredMaterials(materialRows[materialDropdown.idx].material_search).length" class="list-group-item text-muted">
								No matches
							</div>
						</div>
					</form>
				</div>
			</div>

			<div class="card mt-4">
				<div class="card-body">
					<h5 class="card-title mb-3">Sales records</h5>

					<div class="col-md-12 project-list filter-sort">
						<div class="row align-items-end">
							<div class="col-md-6 d-flex mt-5">
								<label class="d-flex listing-length ms-1 align-items-center">
									<span class="me-2">Show</span>
									<select
										class="form-select"
										style="width: auto; min-width: 4.5rem"
										v-model="saleList.listing_length"
										@change="fetchSaleList()"
									>
										<option value="5">5</option>
										<option value="10">10</option>
										<option value="20">20</option>
										<option value="50">50</option>
									</select>
									<span class="ms-2">entries</span>
								</label>
							</div>
							<div class="col-md-2 mt-2 position-relative">
								<label class="form-label small mb-1">Dealer</label>
								<input
									type="text"
									class="form-control"
									v-model="saleListDealerSearch"
									placeholder="Search dealer…"
									@focus="showSaleListDealerSuggestions = true"
									@input="showSaleListDealerSuggestions = true"
									@blur="hideSaleListDealerSuggestions"
									autocomplete="off"
								/>
								<div
									v-if="showSaleListDealerSuggestions"
									class="list-group position-absolute w-100 shadow-sm"
									style="z-index: 20; max-height: 220px; overflow: auto"
								>
									<button
										type="button"
										class="list-group-item list-group-item-action text-muted"
										@mousedown.prevent="clearSaleListDealer"
									>
										All dealers
									</button>
									<button
										v-for="d in filteredSaleListDealers"
										:key="'sl-d-' + d.id"
										type="button"
										class="list-group-item list-group-item-action"
										@mousedown.prevent="selectSaleListDealer(d)"
									>
										{{ d.first_name }}
									</button>
									<div v-if="!filteredSaleListDealers.length" class="list-group-item text-muted">No matches</div>
								</div>
								<!-- <small v-if="saleListSelectedDealer" class="text-muted d-block mt-1">
									Selected: {{ saleListSelectedDealer.first_name }}
									<a href="#" class="ms-1 text-danger" @click.prevent="clearSaleListDealer">clear</a>
								</small> -->
							</div>
							<div class="col-md-2 mt-2">
								<label class="form-label small mb-1">Date</label>
								<input class="form-control" type="date" v-model="saleList.date" @change="fetchSaleList()" />
							</div>
							<div class="col-md-2 mt-2">
								<label class="form-label small mb-1">Search</label>
								<input
									class="form-control"
									type="text"
									placeholder="Invoice number..."
									v-model="saleList.keyword"
									@input="onSaleListKeywordInput"
								/>
							</div>
						</div>
					</div>

					<div class="table-responsive product-table mt-3">
						<table class="display table table-sm">
							<thead>
								<tr align="center">
									<th>Sl.No</th>
									<th>Amount</th>
									<th>Payment Status</th>
									<th>Transaction type</th>
									<th>Transaction amount</th>
									<th>User type</th>
									<th>User & Shipping address</th>
									<th>Bank</th>
									<th>Products</th>
									<th>Invoice</th>
									<th>Remarks</th>
									<th>Added by</th>
									<th>Payment ID</th>
									<th>Sale on</th>
									<th>Action</th>
								</tr>
							</thead>
							<tbody v-if="saleList.loading">
								<tr>
									<td colspan="14" align="center">
										<span class="spinner-border spinner-border-sm me-2"></span>
										Loading…
									</td>
								</tr>
							</tbody>
							<tbody v-else-if="saleList.rows.length">
								<tr align="center" v-for="(row, index) in saleList.rows" :key="'sl-' + row.id">
									<td>{{ saleList.from != null ? saleList.from + index : index + 1 }}</td>
									<td class="text-start small">
										<div><span class="text-muted">Amount:</span> ₹{{ formatMoney(row.amount) }}</div>
										<div><span class="text-muted">GST:</span> ₹{{ formatMoney(row.gst) }}</div>
										<div class="fw-bold"><span class="text-muted">Total:</span> ₹{{ formatMoney(row.total) }}</div>
									</td>
									<td>
										<span class="badge rounded-pill" :class="salePaymentStatusClass(row.payment_status)">{{ paymentStatusRecordLabel(row.payment_status) }}</span>
									</td>
									<td>
										<span class="badge rounded-pill bg-info">{{ transactionTypeRecordLabel(row.transaction_type) }}</span>
									</td>
									<td class="text-start small">
										<div><span class="text-muted">Paid amount:</span> ₹{{ formatMoney(row.paid_amount) }}</div>
										<div v-if="row.airo_payment_online_amount > 0"><span class="text-muted">Online amount:</span> ₹{{ formatMoney(row.airo_payment_online_amount) }}</div>
										<div v-if="row.airo_payment_cash_amount > 0"><span class="text-muted">Cash amount:</span> ₹{{ formatMoney(row.airo_payment_cash_amount) }}</div>
										<div v-if="row.airo_payment_credit_amount !== null && row.airo_payment_credit_amount !== 0"><span class="text-muted">From Advance:</span> ₹{{ formatMoney(row.airo_payment_credit_amount) }}</div>
										<div><span class="text-muted">Balance amount:</span> ₹{{ formatMoney(row.total - row.paid_amount) }}</div>
									</td>
									<td>{{ userTypeRecordLabel(row.user_type) }}<br><br>
										{{ row.dealer_first_name }} {{ row.dealer_last_name }}
									</td>
									<td class="text-start small">
										<div>{{ row.user_name || "—" }}</div>
										<div class="text-muted">{{ row.user_mobile || "—" }}</div> <br>
										<div><span class="text-muted">Shipping address:</span>{{ row.ship_to_address || "—" }}</div>
									</td>
									<td class="text-start small">
										<template v-if="String(row.payment_type) === '3'">
											<div><span class="text-muted">From:</span> {{ row.from_account_bank_name || "—" }}</div>
											<div><span class="text-muted">To:</span> {{ row.to_account_bank_name || "—" }}</div>
										</template>
										<template v-else>
											{{ row.to_account_bank_name || row.airo_payment_account_bank_name || "—" }}
										</template>
									</td>
									<td class="text-start small">
										<div v-for="ps in row.product_sales || []" :key="'ps-' + ps.id">
											<template v-if="Number(ps.category) === 3">{{ saleItemLabel(ps) }}</template>
											<template v-else>{{ saleItemLabel(ps) }} × {{ ps.quantity }}</template>
										</div>
										<span v-if="!(row.product_sales && row.product_sales.length)">—</span>
									</td>
									<td class="text-start small">
										<div>{{ row.invoice_number || "—" }}</div>
										<div class="text-muted">{{ row.invoice_date ? $filters.formatDate(row.invoice_date) : "—" }}</div>
									</td>
									<td class="text-start small">{{ row.remarks || "—" }}</td>
									<td class="text-start small">{{ row.added_by_name || "—" }}</td>
									<td class="text-start small">{{ row.payment_id || "—" }}</td>
									<td>{{ row.invoice_date ? $filters.formatDate(row.invoice_date) : "—" }}</td>
									<td>
										<button type="button" class="btn btn-sm btn-outline-primary me-1 mb-1" @click="$router.push({ name: 'edit_sale', params: { id: row.id } })">
											Edit
										</button>
										<button
											v-if="canCompleteSalePayment(row)"
											type="button"
											class="btn btn-sm btn-outline-success me-1 mb-1"
											@click="openSaleRePaymentModal(row)"
										>
											Complete Payment
										</button>
										<button type="button" class="btn btn-sm btn-outline-secondary mb-1" @click="openSalesPaymentLogsModal(row)">
											Payment Logs
										</button>
									</td>
								</tr>
							</tbody>
							<tbody v-else>
								<tr>
									<td colspan="14" align="center">No records found</td>
								</tr>
							</tbody>
						</table>
					</div>

					<caption v-if="saleList.rows.length" class="float-start mt-2 d-block">
						Showing {{ saleList.from }} to {{ saleList.to }} of {{ saleList.total }} entries
					</caption>
					<nav v-if="saleList.rows.length && saleList.pagination.length" aria-label="Sales pagination" class="mt-2 float-end">
						<ul class="pagination mb-0">
							<li
								class="page-item"
								v-bind:class="[{ disabled: !page.url }, { active: page.active }]"
								v-for="(page, index) in saleList.pagination"
								:key="'sp-' + index + '-' + page.label"
							>
								<a class="page-link" href="#" @click.prevent="onSaleListPageClick(page)" tabindex="-1">
									<span v-if="index === 0">Prev</span>
									<span v-else-if="index === saleList.pagination.length - 1">Next</span>
									<span v-else>{{ page.label }}</span>
								</a>
							</li>
						</ul>
					</nav>
				</div>
			</div>
		</div>

		<Teleport to="body">
			<div
				v-if="salePaymentLogsModal.open && salePaymentLogsModal.row"
				class="modal fade show d-block"
				tabindex="-1"
				role="dialog"
				aria-modal="true"
				style="background-color: rgba(0, 0, 0, 0.5)"
				@click.self="closeSalesPaymentLogsModal"
			>
				<div class="modal-dialog modal-xl modal-dialog-scrollable" @click.stop>
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Payment log</h5>
							<button type="button" class="btn-close" aria-label="Close" @click="closeSalesPaymentLogsModal"></button>
						</div>
						<div class="modal-body">
							<div class="small text-muted mb-3">
								<div><strong>Transaction:</strong> {{ transactionTypeRecordLabel(salePaymentLogsModal.row.transaction_type) }}</div>
								<div><strong>User:</strong> {{ salePaymentLogsModal.row.user_name || "—" }}</div>
								<div><strong>Bank:</strong> {{ salePaymentLogsModal.row.to_account_bank_name || salePaymentLogsModal.row.from_account_bank_name || "—" }}</div>
								<div><strong>Total:</strong> ₹{{ formatMoney(salePaymentLogsModal.row.total) }}</div>
								<div><strong>Paid:</strong> ₹{{ formatMoney(salePaymentLogsModal.row.paid_amount) }}</div>
								<div><strong>Balance:</strong> ₹{{ formatMoney(salePaymentPendingAmount(salePaymentLogsModal.row)) }}</div>
								<div><strong>Invoice:</strong> {{ salePaymentLogsModal.row.invoice_number || "—" }}</div>
								<div v-if="salePaymentLogsModal.row.payment_status != null">
									<strong>Payment status:</strong>
									<span class="badge rounded-pill ms-1" :class="salePaymentStatusClass(salePaymentLogsModal.row.payment_status)">
										{{ salePaymentStatusLabel(salePaymentLogsModal.row.payment_status) }}
									</span>
								</div>
							</div>

							<div v-if="salePaymentLogsModal.loading" class="text-center py-4">
								<span class="spinner-border spinner-border-sm me-2"></span>
								Loading payment logs…
							</div>
							<p v-else-if="salePaymentLogsModal.error" class="text-danger mb-0">{{ salePaymentLogsModal.error }}</p>
							<p v-else-if="!salePaymentLogsModal.logs.length" class="text-muted mb-0">No payment logs for this record.</p>
							<div v-else class="table-responsive">
								<table class="table table-sm table-bordered align-middle">
									<thead class="table-light">
										<tr align="center">
											<th>#</th>
											<th>Transaction amount (₹)</th>
											<th>Status</th>
											<th>Pending (₹)</th>
											<th>Credit (₹)</th>
											<th>Total (₹)</th>
											<th>Bank</th>
											<th>Payment ID</th>
											<th>Payment date</th>
											<th>Recorded on</th>
											<th>Action</th>
										</tr>
									</thead>
									<tbody>
										<tr v-for="(log, idx) in salePaymentLogsModal.logs" :key="'spl-' + log.id" align="center">
											<td>{{ idx + 1 }}</td>
											<td>₹{{ formatMoney(log.transaction_amount) }}</td>
											<td>
												<span class="badge rounded-pill" :class="salePaymentStatusClass(log.status)">
													{{ salePaymentStatusLabel(log.status) }}
												</span>
											</td>
											<td>₹{{ formatMoney(log.pending_amount) }}</td>
											<td>₹{{ formatMoney(log.credit_amount) }}</td>
											<td>₹{{ formatMoney(log.total_amount) }}</td>
											<td class="text-start small">{{ log.bank_name || "—" }}</td>
											<td class="text-start small">{{ log.payment_id || "—" }}</td>
											<td>{{ log.payment_date ? $filters.formatDate(log.payment_date) : "—" }}</td>
											<td class="text-start small">{{ log.created_at ? $filters.formatDate(log.created_at) : "—" }}</td>
											<td>
												<button
													v-if="Number(log.status) !== 2 && $canRevoke()"
													type="button"
													class="btn btn-sm btn-outline-danger"
													@click="openRevokeSaleLogModal(log)"
												>
													Revoke
												</button>
												<span v-else>—</span>
											</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-light" @click="closeSalesPaymentLogsModal">Close</button>
						</div>
					</div>
				</div>
			</div>

			<div
				v-if="revokeSaleLogModal.open && revokeSaleLogModal.log"
				class="modal fade show d-block"
				tabindex="-1"
				role="dialog"
				aria-modal="true"
				style="background-color: rgba(0, 0, 0, 0.5); z-index: 1060"
				@click.self="closeRevokeSaleLogModal"
			>
				<div class="modal-dialog modal-dialog-scrollable" style="z-index: 1061" @click.stop>
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Revoke payment log</h5>
							<button type="button" class="btn-close" aria-label="Close" @click="closeRevokeSaleLogModal"></button>
						</div>
						<div class="modal-body">
							<p class="mb-3">Are you sure you want to revoke this payment log?</p>
							<div class="small border rounded p-3 bg-light text-dark">
								<!-- <div class="mb-1"><strong>Payment log ID:</strong> #{{ revokeSaleLogModal.log.id }}</div> -->
								<div class="mb-1"><strong>Transaction amount:</strong> ₹{{ formatMoney(revokeSaleLogModal.log.transaction_amount) }}</div>
								<div class="mb-1"><strong>Status:</strong> {{ salePaymentStatusLabel(revokeSaleLogModal.log.status) }}</div>
								<div class="mb-1"><strong>Pending:</strong> ₹{{ formatMoney(revokeSaleLogModal.log.pending_amount) }}</div>
								<div class="mb-1"><strong>Credit:</strong> ₹{{ formatMoney(revokeSaleLogModal.log.credit_amount) }}</div>
								<div class="mb-1"><strong>Total:</strong> ₹{{ formatMoney(revokeSaleLogModal.log.total_amount) }}</div>
								<div class="mb-1"><strong>Bank:</strong> {{ revokeSaleLogModal.log.bank_name || "—" }}</div>
								<div class="mb-1"><strong>Payment ID:</strong> {{ revokeSaleLogModal.log.payment_id || "—" }}</div>
								<div class="mb-1"><strong>Payment date:</strong> {{ revokeSaleLogModal.log.payment_date ? $filters.formatDate(revokeSaleLogModal.log.payment_date) : "—" }}</div>
								<div class="mb-0"><strong>Recorded on:</strong> {{ revokeSaleLogModal.log.created_at ? $filters.formatDate(revokeSaleLogModal.log.created_at) : "—" }}</div>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-danger" :disabled="revokeSaleLogModal.submitting" @click="confirmRevokeSale">
								<span v-if="revokeSaleLogModal.submitting" class="spinner-border spinner-border-sm me-2"></span>
								Yes, revoke
							</button>
							<button type="button" class="btn btn-light" :disabled="revokeSaleLogModal.submitting" @click="closeRevokeSaleLogModal">No</button>
						</div>
					</div>
				</div>
			</div>

			<div
				v-if="saleRePaymentModal.open && saleRePaymentModal.row"
				class="modal fade show d-block"
				tabindex="-1"
				role="dialog"
				aria-modal="true"
				style="background-color: rgba(0, 0, 0, 0.5)"
				@click.self="closeSaleRePaymentModal"
			>
				<div class="modal-dialog modal-lg modal-dialog-scrollable" @click.stop>
					<div class="modal-content">
						<div class="modal-header">
							<h5 class="modal-title">Complete sale payment</h5>
							<button type="button" class="btn-close" aria-label="Close" @click="closeSaleRePaymentModal"></button>
						</div>
						<form @submit.prevent="submitSaleRePayment">
							<div class="modal-body">
								<div class="row text-center small mb-3">
									<div class="col-md-4">
										<span class="text-muted d-block">Sale total</span>
										<span class="fw-bold">₹{{ formatMoney(saleRePaymentModal.row.total) }}</span>
									</div>
									<div class="col-md-4">
										<span class="text-muted d-block">Paid</span>
										<span class="fw-bold">₹{{ formatMoney(saleRePaymentModal.row.paid_amount) }}</span>
									</div>
									<div class="col-md-4">
										<span class="text-muted d-block">Pending</span>
										<span class="fw-bold text-warning">₹{{ formatMoney(salePaymentPendingAmount(saleRePaymentModal.row)) }}</span>
									</div>
								</div>
								<div class="small text-muted mb-3">
									<div><strong>User:</strong> {{ saleRePaymentModal.row.user_name || "—" }} {{ saleRePaymentModal.row.user_mobile ? '(' + saleRePaymentModal.row.user_mobile + ')' : '' }}</div>
									<div><strong>Invoice:</strong> {{ saleRePaymentModal.row.invoice_number || "—" }}</div>
								</div>

								<div class="row">
									<div class="col-md-6 form-group mt-2">
										<label class="control-label">Payment status<span class="text-danger">*</span></label>
										<select class="form-select" v-model="saleRePaymentModal.form.payment_status" @change="onSaleRePaymentStatusChange">
											<option value="">— Select —</option>
											<option value="1">Received</option>
											<option value="4">Advance</option>
										</select>
										<div v-if="saleRePaymentModal.errors.payment_status" class="text-danger small mt-1">{{ saleRePaymentModal.errors.payment_status }}</div>
									</div>

									<div class="col-md-6 form-group mt-2" v-if="saleRePaymentModal.form.payment_status === '4'">
										<label class="control-label">Amount from advance<span class="text-danger">*</span></label>
										<input
											class="form-control"
											type="number"
											min="0"
											step="0.01"
											v-model="saleRePaymentModal.form.amount_from_credit"
											placeholder="0.00"
										/>
										<div v-if="saleRePaymentModal.userCreditLoading" class="mt-1"><small class="text-muted">Loading advance balance…</small></div>
										<div v-else-if="saleRePaymentModal.userCredit && saleRePaymentModal.userCredit.credit_available_amount != null" class="mt-1">
											<small class="text-success">Advance available: ₹{{ saleRePaymentModal.userCredit.credit_available_amount }}</small>
										</div>
										<div v-if="saleRePaymentModal.userCreditError" class="text-danger small mt-1">{{ saleRePaymentModal.userCreditError }}</div>
										<div v-if="saleRePaymentModal.errors.amount_from_credit" class="text-danger small mt-1">{{ saleRePaymentModal.errors.amount_from_credit }}</div>
									</div>
								</div>

								<div class="row" v-if="saleRePaymentModal.form.payment_status === '1'">
									<div class="col-md-6 form-group mt-2">
										<label class="control-label">Payment type<span class="text-danger">*</span></label>
										<select class="form-select" v-model="saleRePaymentModal.form.payment_type" @change="onSaleRePaymentTypeChange">
											<option value="">— Select —</option>
											<option value="1">Cash</option>
											<option value="2">Online</option>
											<option value="3">Both</option>
										</select>
										<div v-if="saleRePaymentModal.errors.payment_type" class="text-danger small mt-1">{{ saleRePaymentModal.errors.payment_type }}</div>
									</div>
								</div>

								<div class="row" v-if="saleRePaymentModal.form.payment_status === '1' && (saleRePaymentModal.form.payment_type === '2' || saleRePaymentModal.form.payment_type === '3')">
									<div class="col-md-4 form-group mt-2">
										<label class="control-label">Bank account<span class="text-danger">*</span></label>
										<select class="form-select" v-model="saleRePaymentModal.form.bank_account_id">
											<option value="">— Select bank —</option>
											<option v-for="b in bankAccounts" :key="'srp-' + b.id" :value="String(b.id)">
												{{ b.bank_name }} — {{ b.account_number }}
											</option>
										</select>
										<div v-if="saleRePaymentModal.errors.bank_account_id" class="text-danger small mt-1">{{ saleRePaymentModal.errors.bank_account_id }}</div>
									</div>
									<div class="col-md-4 form-group mt-2">
										<label class="control-label">Online amount (₹)<span class="text-danger">*</span></label>
										<input
											class="form-control"
											type="number"
											min="0"
											step="0.01"
											v-model="saleRePaymentModal.form.cash_online"
											placeholder="0.00"
										/>
										<div v-if="saleRePaymentModal.errors.cash_online" class="text-danger small mt-1">{{ saleRePaymentModal.errors.cash_online }}</div>
									</div>
									<div class="col-md-4 form-group mt-2">
										<label class="control-label">Payment / transaction ID</label>
										<input class="form-control" type="text" v-model="saleRePaymentModal.form.payment_id" placeholder="UTR / reference" />
										<div v-if="saleRePaymentModal.errors.payment_id" class="text-danger small mt-1">{{ saleRePaymentModal.errors.payment_id }}</div>
									</div>
								</div>

								<div class="row" v-if="saleRePaymentModal.form.payment_status === '1' && (saleRePaymentModal.form.payment_type === '1' || saleRePaymentModal.form.payment_type === '3')">
									<div class="col-md-4 form-group mt-2">
										<label class="control-label">Cash Amount (₹)<span class="text-danger">*</span></label>
										<input
											class="form-control"
											type="number"
											min="0"
											step="0.01"
											v-model="saleRePaymentModal.form.cash_in_hand"
											placeholder="0.00"
										/>
										<div v-if="saleRePaymentModal.errors.cash_in_hand" class="text-danger small mt-1">{{ saleRePaymentModal.errors.cash_in_hand }}</div>
									</div>
								</div>

								<div class="row" v-if="saleRePaymentBreakdown">
									<div class="col-md-12">
										<div class="card border-light mt-2">
											<div class="card-body py-2">
												<div class="row text-center small">
													<div class="col-md-3">
														<span class="text-muted d-block">Pending</span>
														<span class="fw-bold">₹{{ saleRePaymentBreakdown.pending }}</span>
													</div>
													<div class="col-md-3" v-if="saleRePaymentModal.form.payment_type === '2' || saleRePaymentModal.form.payment_type === '3'">
														<span class="text-muted d-block">Online</span>
														<span class="fw-bold">₹{{ saleRePaymentBreakdown.online }}</span>
													</div>
													<div class="col-md-3" v-if="saleRePaymentModal.form.payment_type === '1' || saleRePaymentModal.form.payment_type === '3'">
														<span class="text-muted d-block">Cash</span>
														<span class="fw-bold">₹{{ saleRePaymentBreakdown.cash }}</span>
													</div>
													<div class="col-md-3">
														<span class="text-muted d-block">Match</span>
														<span class="fw-bold" :class="saleRePaymentBreakdown.statusClass">{{ saleRePaymentBreakdown.statusLabel }}</span>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>

								<div class="row">
									<div class="col-md-4 form-group mt-2" v-if="saleRePaymentModal.form.payment_status === '1' || saleRePaymentModal.form.payment_status === '4'">
										<label class="control-label">Payment date<span class="text-danger">*</span></label>
										<input class="form-control" type="date" v-model="saleRePaymentModal.form.payment_date" />
										<div v-if="saleRePaymentModal.errors.payment_date" class="text-danger small mt-1">{{ saleRePaymentModal.errors.payment_date }}</div>
									</div>
								</div>
							</div>
							<div class="modal-footer">
								<button type="button" class="btn btn-light" @click="closeSaleRePaymentModal" :disabled="saleRePaymentModal.submitting">Cancel</button>
								<button type="submit" class="btn btn-primary" :disabled="saleRePaymentModal.submitting">
									<span v-if="saleRePaymentModal.submitting" class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
									{{ saleRePaymentModal.submitting ? "Saving..." : "Submit payment" }}
								</button>
							</div>
						</form>
					</div>
				</div>
			</div>
		</Teleport>
	</div>
</template>

<script>
export default {
	name: "Sales",
	data() {
		return {
			token: "",
			submitting: false,
			errors: {},

			bankAccounts: [],
			dealers: [],
			customers: [],
			allProducts: [],
			rawMaterials: [],
			materialRows: [],
			materialDropdown: {
				open: false,
				idx: -1,
				top: 0,
				left: 0,
				width: 280,
			},
			closeMaterialDropdownTimer: null,

			dealerSearch: "",
			customerSearch: "",
			selectedDealer: null,
			selectedCustomer: null,
			isNewDealer: false,
			isNewCustomer: false,
			states: [],
			districts: [],
			districtsLoading: false,
			showDealerSuggestions: false,
			showCustomerSuggestions: false,

			userCredit: null,
			userCreditLoading: false,
			userCreditError: "",

			form: {
				category: "",
				product_type: "",
				user_type: "",
				bank_account_id: "",
				payment_status: "",
				payment_type: "",
				cash_online: "",
				cash_in_hand: "",
				amount_from_credit: "",
				quantity: "",
				gst_amount: "0",
				total_amount: "",
				fitment_charge: "0",
				fuel_charge: "0",
				other_charges: "0",
				payment_id: "",
				payment_date: "",
				invoice_number: "",
				invoice_date: "",
				remarks: "",
				user_name: "",
				user_mobile: "",
				dealer_name: "",
				dealer_mobile: "",
				address: "",
				state_id: "",
				district_id: "",
				password: "123456",
				ship_to_address: "",
			},

			saleList: {
				listing_length: "10",
				keyword: "",
				date: "",
				user_id: "",
				rows: [],
				pagination: [],
				from: 0,
				to: 0,
				total: 0,
				loading: false,
				active_page_url: "",
			},
			saleListKeywordDebounceTimer: null,
			saleListDealerSearch: "",
			saleListSelectedDealer: null,
			showSaleListDealerSuggestions: false,

			salePaymentLogsModal: {
				open: false,
				row: null,
				logs: [],
				loading: false,
				error: "",
			},

			revokeSaleLogModal: {
				open: false,
				log: null,
				submitting: false,
			},

			saleRePaymentModal: {
				open: false,
				row: null,
				submitting: false,
				errors: {},
				userCredit: null,
				userCreditLoading: false,
				userCreditError: "",
				form: {
					payment_status: "",
					payment_type: "",
					cash_online: "",
					cash_in_hand: "",
					amount_from_credit: "",
					bank_account_id: "",
					payment_id: "",
					payment_date: "",
				},
			},
		};
	},
	computed: {
		materialDropdownStyle() {
			return {
				position: "fixed",
				top: `${this.materialDropdown.top}px`,
				left: `${this.materialDropdown.left}px`,
				width: `${this.materialDropdown.width}px`,
				zIndex: 2000,
				maxHeight: "260px",
				overflow: "auto",
			};
		},
		showSaleAmountBlock() {
			if (this.form.category === "3") return false;
			if (this.form.category === "2") return true;
			return this.form.category === "1" && Boolean(this.form.product_type);
		},
		isOtherServices() {
			return this.form.category === "3";
		},
		isDealerOrCustomer() {
			return this.form.user_type === "1" || this.form.user_type === "2";
		},
		saleRePaymentBreakdown() {
			const modal = this.saleRePaymentModal;
			if (!modal.open || !modal.row || modal.form.payment_status !== "1" || !modal.form.payment_type) return null;
			const pending = this.salePaymentPendingAmount(modal.row);
			const online = parseFloat(modal.form.cash_online) || 0;
			const cash = parseFloat(modal.form.cash_in_hand) || 0;
			let paid = 0;
			if (modal.form.payment_type === "2") paid = online;
			else if (modal.form.payment_type === "1") paid = cash;
			else if (modal.form.payment_type === "3") paid = online + cash;
			else return null;
			const eps = 0.01;
			const diff = paid - pending;
			let statusLabel = "Partial";
			let statusClass = "text-warning";
			if (Math.abs(diff) < eps) {
				statusLabel = "Full";
				statusClass = "text-success";
			} else if (diff > eps) {
				statusLabel = "Over";
				statusClass = "text-danger";
			}
			return {
				pending: this.formatMoney(pending),
				online: this.formatMoney(online),
				cash: this.formatMoney(cash),
				statusLabel,
				statusClass,
			};
		},
		showShippingAddress() {
			if (this.form.category === "3") return false;
			if (this.form.user_type === "1") return Boolean(this.selectedDealer) || this.isNewDealer;
			if (this.form.user_type === "2") return Boolean(this.selectedCustomer) || this.isNewCustomer;
			return false;
		},
		/** Cash sale: show generic payment ID row (dealer/customer use received/online block). */
		showStandalonePaymentId() {
			return this.form.user_type === "4";
		},
		showPaymentDate() {
			if (this.form.user_type === "4") return true;
			if (!this.isDealerOrCustomer) return false;
			return this.form.payment_status === "1" || this.form.payment_status === "3" || this.form.payment_status === "4";
		},
		isPaymentDateRequired() {
			return this.showPaymentDate;
		},
		saleTotalAmount() {
			const n = parseFloat(this.form.total_amount);
			return isNaN(n) ? 0 : n;
		},
		dealerCustomerPaymentBreakdown() {
			if (this.form.payment_status !== "1" || !this.form.payment_type) return null;
			const total = this.saleTotalAmount;
			const online = parseFloat(this.form.cash_online) || 0;
			const cash = parseFloat(this.form.cash_in_hand) || 0;
			let paid = 0;
			if (this.form.payment_type === "2") paid = online;
			else if (this.form.payment_type === "1") paid = cash;
			else if (this.form.payment_type === "3") paid = online + cash;
			else return null;
			const eps = 0.01;
			const diff = paid - total;
			let statusLabel = "Partial";
			let statusClass = "text-warning";
			if (Math.abs(diff) < eps) {
				statusLabel = "Full";
				statusClass = "text-success";
			} else if (diff > eps) {
				statusLabel = "Over";
				statusClass = "text-info";
			}
			if (this.form.payment_type === "2") {
				return { total, online, cash: 0, statusLabel, statusClass };
			}
			if (this.form.payment_type === "1") {
				return { total, online: 0, cash, statusLabel, statusClass };
			}
			return { total, online, cash, statusLabel, statusClass };
		},
		filteredDealers() {
			const q = String(this.dealerSearch || "").toLowerCase().trim();
			if (!q) return this.dealers.slice(0, 20);
			return this.dealers.filter((d) => String(d.first_name || "").toLowerCase().includes(q)).slice(0, 20);
		},
		filteredSaleListDealers() {
			const q = String(this.saleListDealerSearch || "").toLowerCase().trim();
			if (!q) return this.dealers.slice(0, 20);
			return this.dealers
				.filter((d) => String(d.first_name || "").toLowerCase().includes(q))
				.slice(0, 20);
		},
		filteredCustomers() {
			const q = String(this.customerSearch || "").toLowerCase().trim();
			if (!q) return this.customers.slice(0, 20);
			return this.customers
				.filter((c) => {
					const name = String(c.company_name || "").toLowerCase();
					const mobile = String(c.mobile || "").toLowerCase();
					return name.includes(q) || mobile.includes(q);
				})
				.slice(0, 20);
		},
		salePayerUserId() {
			if (this.form.user_type === "1" && this.selectedDealer) {
				const uid = this.dealerAccountUserId(this.selectedDealer);
				return uid ? String(uid) : "";
			}
			if (this.form.user_type === "2" && this.selectedCustomer?.user_id) {
				return String(this.selectedCustomer.user_id);
			}
			return "";
		},
	},
	watch: {
		salePayerUserId() {
			this.maybeFetchUserCreditForSale();
		},
		"form.payment_status"() {
			this.maybeFetchUserCreditForSale();
		},
	},
	created() {
		this.init();
	},
	beforeDestroy() {
		if (this.saleListKeywordDebounceTimer) {
			clearTimeout(this.saleListKeywordDebounceTimer);
			this.saleListKeywordDebounceTimer = null;
		}
	},
	methods: {
		getToken() {
			if (localStorage.getItem("airotrack_staff")) {
				const airotrack_staff = JSON.parse(localStorage.getItem("airotrack_staff"));
				if (airotrack_staff.token) {
					this.token = airotrack_staff.token;
					return;
				}
			}
			this.$swal("", "Session expired, please login again", "error");
			this.$router.push({ name: "login" });
		},

		async init() {
			this.getToken();
			await Promise.all([
				this.fetchBankAccounts(),
				this.fetchDealers(),
				this.fetchCustomers(),
				this.fetchAllProducts(),
				this.fetchRawMaterials(),
				this.fetchStates(),
			]);
			this.fetchSaleList();
		},

		buildSaleQueryString() {
			const kw = String(this.saleList.keyword || "").trim();
			const date = String(this.saleList.date || "").trim();
			const userId = String(this.saleList.user_id || "").trim();
			return [
				`limit=${encodeURIComponent(this.saleList.listing_length)}`,
				`keyword=${encodeURIComponent(kw)}`,
				`date=${encodeURIComponent(date)}`,
				`user_id=${encodeURIComponent(userId)}`,
			].join("&");
		},

		saleListUrl() {
			return `/staffs/getSale?${this.buildSaleQueryString()}`;
		},

		onSaleListKeywordInput() {
			if (this.saleListKeywordDebounceTimer) {
				clearTimeout(this.saleListKeywordDebounceTimer);
			}
			this.saleListKeywordDebounceTimer = setTimeout(() => {
				this.saleListKeywordDebounceTimer = null;
				this.fetchSaleList();
			}, 400);
		},

		hideSaleListDealerSuggestions() {
			setTimeout(() => {
				this.showSaleListDealerSuggestions = false;
			}, 200);
		},

		selectSaleListDealer(dealer) {
			const userId = this.dealerAccountUserId(dealer);
			if (!userId) {
				this.$swal("Error", "Selected dealer has no linked user account", "error");
				return;
			}
			this.saleListSelectedDealer = { ...dealer, user_id: userId };
			this.saleListDealerSearch = dealer?.first_name || "";
			this.saleList.user_id = String(userId);
			this.showSaleListDealerSuggestions = false;
			this.fetchSaleList();
		},

		clearSaleListDealer() {
			this.saleListSelectedDealer = null;
			this.saleListDealerSearch = "";
			this.saleList.user_id = "";
			this.showSaleListDealerSuggestions = false;
			this.fetchSaleList();
		},

		fetchSaleList(pageUrl) {
			this.getToken();
			this.saleList.loading = true;
			const url = pageUrl || this.saleListUrl();
			this.saleList.active_page_url = url;
			this.$axios
				.get(url, { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const payload = res.data?.data || {};
					const paginator = payload.sale;
					if (!paginator) {
						this.saleList.rows = [];
						this.saleList.pagination = [];
						this.saleList.from = 0;
						this.saleList.to = 0;
						this.saleList.total = 0;
						return;
					}
					this.saleList.rows = paginator.data || [];
					this.saleList.pagination = paginator.links || [];
					this.saleList.from = paginator.from ?? 0;
					this.saleList.to = paginator.to ?? 0;
					this.saleList.total = paginator.total ?? 0;
				})
				.catch((err) => {
					console.error(err);
					this.saleList.rows = [];
					this.saleList.pagination = [];
					this.saleList.from = 0;
					this.saleList.to = 0;
					this.saleList.total = 0;
					this.$swal("Error", err.response?.data?.message || "Failed to load sales list", "error");
				})
				.finally(() => {
					this.saleList.loading = false;
				});
		},

		onSaleListPageClick(page) {
			if (!page.url) return;
			const base = page.url;
			const sep = base.includes("?") ? "&" : "?";
			this.fetchSaleList(`${base}${sep}${this.buildSaleQueryString()}`);
		},

		salePaymentStatusClass(v) {
			const n = Number(v);
			if (n === 1) return "bg-success";
			if (n === 2) return "bg-secondary";
			if (n === 3) return "bg-warning";
			if (n === 4) return "bg-info";
			return "bg-secondary";
		},

		salePaymentStatusLabel(v) {
			const map = { 1: "Paid", 2: "Pending", 3: "Partially Paid", 4: "Credit" };
			return map[Number(v)] || "—";
		},

		salePaymentPendingAmount(row) {
			if (!row) return 0;
			const total = parseFloat(row.total) || 0;
			const paid = parseFloat(row.paid_amount) || 0;
			const fromPayment = parseFloat(row.airo_payment_pending_amount);
			if (!isNaN(fromPayment) && fromPayment > 0.01) {
				return fromPayment;
			}
			return Math.max(0, total - paid);
		},

		canCompleteSalePayment(row) {
			if (!row) return false;
			if (Number(row.payment_status) === 1) return false;
			return this.salePaymentPendingAmount(row) > 0.01;
		},

		todayInputDate() {
			const d = new Date();
			const yyyy = d.getFullYear();
			const mm = String(d.getMonth() + 1).padStart(2, "0");
			const dd = String(d.getDate()).padStart(2, "0");
			return `${yyyy}-${mm}-${dd}`;
		},

		openSaleRePaymentModal(row) {
			this.saleRePaymentModal.row = row;
			this.saleRePaymentModal.submitting = false;
			this.saleRePaymentModal.errors = {};
			this.saleRePaymentModal.userCredit = null;
			this.saleRePaymentModal.userCreditError = "";
			this.saleRePaymentModal.userCreditLoading = false;
			const pending = this.salePaymentPendingAmount(row);
			this.saleRePaymentModal.form = {
				payment_status: "",
				payment_type: "",
				cash_online: "",
				cash_in_hand: "",
				amount_from_credit: pending > 0 ? String(pending) : "",
				bank_account_id: "",
				payment_id: "",
				payment_date: this.todayInputDate(),
			};
			this.saleRePaymentModal.open = true;
		},

		closeSaleRePaymentModal() {
			this.saleRePaymentModal.open = false;
			this.saleRePaymentModal.row = null;
			this.saleRePaymentModal.submitting = false;
			this.saleRePaymentModal.errors = {};
			this.saleRePaymentModal.userCredit = null;
			this.saleRePaymentModal.userCreditError = "";
			this.saleRePaymentModal.userCreditLoading = false;
			this.saleRePaymentModal.form = {
				payment_status: "",
				payment_type: "",
				cash_online: "",
				cash_in_hand: "",
				amount_from_credit: "",
				bank_account_id: "",
				payment_id: "",
				payment_date: "",
			};
		},

		onSaleRePaymentStatusChange() {
			const f = this.saleRePaymentModal.form;
			const pending = this.salePaymentPendingAmount(this.saleRePaymentModal.row);
			f.payment_type = "";
			f.cash_online = "";
			f.cash_in_hand = "";
			f.bank_account_id = "";
			f.payment_id = "";
			f.amount_from_credit = f.payment_status === "4" && pending > 0 ? String(pending) : "";
			this.saleRePaymentModal.errors = {};
			if (f.payment_status === "4") {
				this.fetchUserCreditForSaleRePayment();
			} else {
				this.saleRePaymentModal.userCredit = null;
				this.saleRePaymentModal.userCreditError = "";
			}
		},

		onSaleRePaymentTypeChange() {
			const f = this.saleRePaymentModal.form;
			if (f.payment_type === "2") {
				f.cash_in_hand = "";
			} else if (f.payment_type === "1") {
				f.cash_online = "";
				f.payment_id = "";
				f.bank_account_id = "";
			}
		},

		async fetchUserCreditForSaleRePayment() {
			const row = this.saleRePaymentModal.row;
			const userId = row?.user_id;
			if (!userId) {
				this.saleRePaymentModal.userCredit = null;
				this.saleRePaymentModal.userCreditError = "Sale has no linked user for advance";
				return;
			}
			try {
				this.getToken();
				this.saleRePaymentModal.userCreditLoading = true;
				this.saleRePaymentModal.userCreditError = "";
				const res = await this.$axios.get("/staffs/getUserCredit", {
					params: { user_id: userId },
					headers: { Authorization: "Bearer " + this.token },
				});
				const ok = res.data?.status === true || res.data?.status === "true";
				if (!ok) {
					this.saleRePaymentModal.userCredit = null;
					this.saleRePaymentModal.userCreditError = this.$getApiErrorMessage(res.data, "Failed to fetch advance balance");
					return;
				}
				this.saleRePaymentModal.userCredit = res.data?.data?.user_credit || null;
			} catch (e) {
				this.saleRePaymentModal.userCredit = null;
				this.saleRePaymentModal.userCreditError = this.$getApiErrorMessage(e, "Failed to fetch advance balance");
			} finally {
				this.saleRePaymentModal.userCreditLoading = false;
			}
		},

		validateSaleRePayment() {
			const errors = {};
			const f = this.saleRePaymentModal.form;
			const pending = this.salePaymentPendingAmount(this.saleRePaymentModal.row);

			if (!f.payment_status) {
				errors.payment_status = "Select payment status";
			}
			if (!f.payment_date) {
				errors.payment_date = "Select payment date";
			}

			if (f.payment_status === "1") {
				if (!f.payment_type) {
					errors.payment_type = "Select payment type";
				}
				const online = parseFloat(f.cash_online) || 0;
				const cash = parseFloat(f.cash_in_hand) || 0;
				if (f.payment_type === "2" || f.payment_type === "3") {
					if (!f.bank_account_id) {
						errors.bank_account_id = "Select bank account";
					}
					if (isNaN(parseFloat(f.cash_online)) || online < 0) {
						errors.cash_online = "Enter online amount";
					}
				}
				if (f.payment_type === "1" || f.payment_type === "3") {
					if (isNaN(parseFloat(f.cash_in_hand)) || cash < 0) {
						errors.cash_in_hand = "Enter cash amount";
					}
				}
				let paid = 0;
				if (f.payment_type === "1") paid = cash;
				else if (f.payment_type === "2") paid = online;
				else if (f.payment_type === "3") paid = cash + online;
				if (paid <= 0.01) {
					if (f.payment_type === "1") errors.cash_in_hand = "Amount must be greater than zero";
					else if (f.payment_type === "2") errors.cash_online = "Amount must be greater than zero";
					else errors.cash_in_hand = "Cash + online must be greater than zero";
				} else if (paid > pending + 0.01) {
					errors.payment_type = `Amount cannot exceed pending (₹${this.formatMoney(pending)})`;
				}
			}

			if (f.payment_status === "4") {
				const ac = parseFloat(f.amount_from_credit);
				if (isNaN(ac) || ac <= 0) {
					errors.amount_from_credit = "Enter amount from advance";
				} else if (ac > pending + 0.01) {
					errors.amount_from_credit = `Cannot exceed pending (₹${this.formatMoney(pending)})`;
				} else if (this.saleRePaymentModal.userCredit?.credit_available_amount != null) {
					const avail = parseFloat(this.saleRePaymentModal.userCredit.credit_available_amount);
					if (!isNaN(avail) && ac > avail + 0.01) {
						errors.amount_from_credit = "Amount cannot exceed available advance";
					}
				}
			}

			this.saleRePaymentModal.errors = errors;
			return Object.keys(errors).length === 0;
		},

		submitSaleRePayment() {
			if (!this.validateSaleRePayment()) return;
			const f = this.saleRePaymentModal.form;
			const row = this.saleRePaymentModal.row;
			const payload = {
				id: row.id,
				payment_status: parseInt(f.payment_status, 10),
				payment_date: f.payment_date,
				payment_type: null,
				cash_online: null,
				cash_in_hand: null,
				amount_from_credit: null,
				bank_account_id: null,
				payment_id: null,
			};

			if (f.payment_status === "1") {
				payload.payment_type = parseInt(f.payment_type, 10);
				if (f.payment_type === "2" || f.payment_type === "3") {
					payload.bank_account_id = parseInt(f.bank_account_id, 10);
					payload.cash_online = parseFloat(f.cash_online);
					payload.payment_id = String(f.payment_id || "").trim() || null;
				}
				if (f.payment_type === "1" || f.payment_type === "3") {
					payload.cash_in_hand = parseFloat(f.cash_in_hand);
				}
			} else if (f.payment_status === "4") {
				payload.amount_from_credit = parseFloat(f.amount_from_credit);
			}

			this.getToken();
			this.saleRePaymentModal.submitting = true;
			this.$axios
				.post("/staffs/saleRePayment", payload, {
					headers: { Authorization: "Bearer " + this.token },
				})
				.then((res) => {
					const ok = res.data?.status === true || res.data?.status === "true";
					if (ok) {
						this.$swal("Success", this.$extractApiMessage(res.data?.message) || "Payment completed", "success");
						this.closeSaleRePaymentModal();
						this.fetchSaleList(this.saleList.active_page_url || undefined);
						return;
					}
					this.$applyApiFieldErrors(this.saleRePaymentModal.errors, res.data);
					this.$swal("Error", this.$getApiErrorMessage(res.data, "Failed to complete payment"), "error");
				})
				.catch((err) => {
					this.$applyApiFieldErrors(this.saleRePaymentModal.errors, err);
					this.$swal("Error", this.$getApiErrorMessage(err, "Something went wrong"), "error");
				})
				.finally(() => {
					this.saleRePaymentModal.submitting = false;
				});
		},

		openSalesPaymentLogsModal(row) {
			this.salePaymentLogsModal.row = row;
			this.salePaymentLogsModal.open = true;
			this.salePaymentLogsModal.logs = [];
			this.salePaymentLogsModal.error = "";
			this.fetchSalesPaymentLogs(row.id);
		},

		closeSalesPaymentLogsModal() {
			this.closeRevokeSaleLogModal();
			this.salePaymentLogsModal.open = false;
			this.salePaymentLogsModal.row = null;
			this.salePaymentLogsModal.logs = [];
			this.salePaymentLogsModal.loading = false;
			this.salePaymentLogsModal.error = "";
		},

		fetchSalesPaymentLogs(transactionId) {
			this.getToken();
			this.salePaymentLogsModal.loading = true;
			this.salePaymentLogsModal.error = "";
			this.$axios
				.get(`/staffs/salesPaymentLogs?transaction_id=${encodeURIComponent(transactionId)}`, {
					headers: { Authorization: "Bearer " + this.token },
				})
				.then((res) => {
					const data = res.data?.data || res.data || {};
					const logs = data.airo_payment_logs;
					this.salePaymentLogsModal.logs = Array.isArray(logs) ? logs : [];
				})
				.catch((err) => {
					this.salePaymentLogsModal.logs = [];
					this.salePaymentLogsModal.error = err.response?.data?.message || "Failed to load payment logs";
				})
				.finally(() => {
					this.salePaymentLogsModal.loading = false;
				});
		},

		openRevokeSaleLogModal(log) {
			if (!this.$canRevoke()) return;
			this.revokeSaleLogModal.log = log;
			this.revokeSaleLogModal.open = true;
			this.revokeSaleLogModal.submitting = false;
		},

		closeRevokeSaleLogModal() {
			this.revokeSaleLogModal.open = false;
			this.revokeSaleLogModal.log = null;
			this.revokeSaleLogModal.submitting = false;
		},

		isApiSuccess(data) {
			return data?.status === true || data?.status === "true" || data?.success === true || data?.success === "true";
		},

		async confirmRevokeSale() {
			if (!this.revokeSaleLogModal.log?.id) return;
			this.getToken();
			this.revokeSaleLogModal.submitting = true;
			const formData = new FormData();
			formData.append("airo_payment_log_id", this.revokeSaleLogModal.log.id);
			const saleRowId = this.salePaymentLogsModal.row?.id;
			try {
				const res = await this.$axios.post("/staffs/revokeSale", formData, {
					headers: {
						"content-type": "multipart/form-data",
						Authorization: "Bearer " + this.token,
					},
				});
				if (this.isApiSuccess(res.data)) {
					this.$swal("Success", res.data?.message || "Payment log revoked", "success");
					this.closeRevokeSaleLogModal();
					if (saleRowId) {
						this.fetchSalesPaymentLogs(saleRowId);
					}
					this.fetchSaleList(this.saleList.active_page_url);
				} else {
					this.$swal("Error", res.data?.message || "Failed to revoke payment log", "error");
				}
			} catch (err) {
				this.$swal("Error", err?.response?.data?.message || "Failed to revoke payment log", "error");
			} finally {
				this.revokeSaleLogModal.submitting = false;
			}
		},

		formatMoney(value) {
			const n = parseFloat(value);
			if (isNaN(n)) return "0.00";
			return n.toFixed(2);
		},

		paymentTypeRecordLabel(v) {
			const map = { 1: "Credit", 2: "Debit", 3: "Contra" };
			return map[Number(v)] || "—";
		},

		paymentStatusRecordLabel(v) {
			const map = { 1: "Received", 2: "Pending", 3: "Partially", 4: "Credit" };
			return map[Number(v)] || "—";
		},

		transactionTypeRecordLabel(v) {
			const map = { 1: "Contra", 2: "Deposit", 3: "Purchase", 4: "Sale", 17: "Other Service Sales" };
			return map[Number(v)] || "—";
		},

		userTypeRecordLabel(v) {
			const map = { 1: "Dealer", 2: "Customer", 3: "Vendor", 4: "Party", 5: "Staff" };
			return map[Number(v)] || "—";
		},

		productTypeRecordLabel(t) {
			const product = this.allProducts.find((p) => String(p.id) === String(t));
			return product?.product_name || "—";
		},

		saleItemLabel(ps) {
			if (Number(ps?.category) === 3) {
				const parts = [];
				const fitment = parseFloat(ps?.fitment_charge);
				const fuel = parseFloat(ps?.fuel_charge);
				const other = parseFloat(ps?.other_charges);
				if (!isNaN(fitment) && fitment > 0) parts.push("Fitment ₹" + this.formatMoney(fitment));
				if (!isNaN(fuel) && fuel > 0) parts.push("Fuel ₹" + this.formatMoney(fuel));
				if (!isNaN(other) && other > 0) parts.push("Other ₹" + this.formatMoney(other));
				return parts.length ? "Other services — " + parts.join(", ") : "Other services";
			}
			if (Number(ps?.category) === 2) {
				return ps?.raw_material?.raw_material_name || ps?.raw_material_name || "Raw material";
			}
			return this.productTypeRecordLabel(ps?.product_type);
		},

		fetchAllProducts() {
			return this.$axios
				.get("/staffs/allProducts", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.allProducts = Array.isArray(data) ? data : data?.data || [];
				})
				.catch(() => {
					this.allProducts = [];
				});
		},

		fetchRawMaterials() {
			return this.$axios
				.get("/staffs/getRawMaterials", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const d = res.data?.data || res.data;
					if (Array.isArray(d)) {
						this.rawMaterials = d;
					} else if (Array.isArray(d?.data)) {
						this.rawMaterials = d.data;
					} else if (Array.isArray(d?.rawMaterials)) {
						this.rawMaterials = d.rawMaterials;
					} else {
						this.rawMaterials = [];
					}
				})
				.catch(() => {
					this.rawMaterials = [];
				});
		},

		fetchBankAccounts() {
			return this.$axios
				.get("/staffs/bankAccounts", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.bankAccounts = Array.isArray(data) ? data : data?.data || [];
				})
				.catch(() => {
					this.bankAccounts = [];
				});
		},

		fetchDealers() {
			return this.$axios
				.get("/staffs/dealers", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					const list = Array.isArray(data) ? data : data?.data || [];
					this.dealers = list.map((d) => ({
						...d,
						user_id: d.user_id ?? d.user?.id ?? null,
					}));
				})
				.catch(() => {
					this.dealers = [];
				});
		},

		fetchCustomers() {
			return this.$axios
				.get("/staffs/companies", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data.data || res.data;
					this.customers = Array.isArray(data) ? data : data?.data || [];
				})
				.catch(() => {
					this.customers = [];
				});
		},

		fetchStates() {
			return this.$axios
				.get("/staffs/get_states", { headers: { Authorization: "Bearer " + this.token } })
				.then((res) => {
					const data = res.data?.data || res.data;
					this.states = Array.isArray(data?.states) ? data.states : [];
				})
				.catch(() => {
					this.states = [];
				});
		},

		fetchDistricts(stateId) {
			if (!stateId) {
				this.districts = [];
				return Promise.resolve();
			}
			this.districtsLoading = true;
			return this.$axios
				.get("/staffs/getDistricts", {
					params: { state_id: stateId },
					headers: { Authorization: "Bearer " + this.token },
				})
				.then((res) => {
					const data = res.data?.data || res.data;
					this.districts = Array.isArray(data?.districts) ? data.districts : [];
				})
				.catch(() => {
					this.districts = [];
				})
				.finally(() => {
					this.districtsLoading = false;
				});
		},

		hideDealerSuggestions() {
			setTimeout(() => {
				this.showDealerSuggestions = false;
			}, 200);
		},
		hideCustomerSuggestions() {
			setTimeout(() => {
				this.showCustomerSuggestions = false;
			}, 200);
		},

		dealerAccountUserId(dealer) {
			if (!dealer) return null;
			const uid = dealer.user_id ?? dealer.user?.id;
			if (uid == null || uid === "") return null;
			const n = parseInt(uid, 10);
			return isNaN(n) ? null : n;
		},

		userAddressFromSelection(entity) {
			if (!entity) return "";
			const addr = entity.address ?? entity.ship_to_address ?? "";
			return String(addr || "").trim();
		},

		selectDealer(dealer) {
			const userId = this.dealerAccountUserId(dealer);
			if (!userId) {
				this.$swal("Error", "Selected dealer has no linked user account", "error");
				return;
			}
			this.selectedDealer = { ...dealer, user_id: userId };
			this.dealerSearch = dealer?.first_name || "";
			this.showDealerSuggestions = false;
			this.form.ship_to_address = this.userAddressFromSelection(dealer);
			this.clearDealerCustomerPaymentFields();
		},
		selectCustomer(c) {
			this.selectedCustomer = c;
			this.customerSearch = c?.company_name || "";
			this.showCustomerSuggestions = false;
			this.form.ship_to_address = this.userAddressFromSelection(c);
			this.clearDealerCustomerPaymentFields();
		},

		enableNewCustomer() {
			this.isNewCustomer = true;
			this.selectedCustomer = null;
			this.customerSearch = "";
			this.showCustomerSuggestions = false;
			this.form.user_name = "";
			this.form.user_mobile = "";
			this.form.password = "123456";
			this.form.ship_to_address = "";
			delete this.errors.customer_id;
			this.clearDealerCustomerPaymentFields();
		},

		enableNewDealer() {
			this.isNewDealer = true;
			this.selectedDealer = null;
			this.dealerSearch = "";
			this.showDealerSuggestions = false;
			this.clearNewDealerFields();
			this.form.password = "123456";
			this.form.ship_to_address = "";
			delete this.errors.dealer_id;
			this.clearDealerCustomerPaymentFields();
			if (!this.states.length) {
				this.fetchStates();
			}
		},

		selectExistingCustomer() {
			this.isNewCustomer = false;
			this.clearNewCustomerFields();
			this.form.ship_to_address = "";
			this.clearDealerCustomerPaymentFields();
		},

		selectExistingDealer() {
			this.isNewDealer = false;
			this.clearNewDealerFields();
			this.form.ship_to_address = "";
			this.clearDealerCustomerPaymentFields();
		},

		onDealerStateChange() {
			this.form.district_id = "";
			this.districts = [];
			if (this.form.state_id) {
				this.fetchDistricts(this.form.state_id);
			}
		},

		syncDealerAddressToShipping() {
			if (this.isNewDealer) {
				this.form.ship_to_address = String(this.form.address || "").trim();
			}
		},

		clearNewCustomerFields() {
			this.form.user_name = "";
			this.form.user_mobile = "";
			this.form.password = "123456";
			delete this.errors.user_name;
			delete this.errors.user_mobile;
			delete this.errors.password;
		},

		clearNewDealerFields() {
			this.form.dealer_name = "";
			this.form.dealer_mobile = "";
			this.form.address = "";
			this.form.state_id = "";
			this.form.district_id = "";
			this.form.password = "123456";
			this.districts = [];
			delete this.errors.dealer_name;
			delete this.errors.dealer_mobile;
			delete this.errors.address;
			delete this.errors.state_id;
			delete this.errors.district_id;
			delete this.errors.password;
		},

		productTypeLabel(t) {
			const product = this.allProducts.find((p) => String(p.id) === String(t));
			return product?.product_name || "Product";
		},

		onCategoryChange() {
			this.errors = {};
			this.form.product_type = "";
			this.form.quantity = "";
			this.form.total_amount = "";
			this.form.fitment_charge = "0";
			this.form.fuel_charge = "0";
			this.form.other_charges = "0";
			this.form.gst_amount = "0";
			this.form.payment_status = "";
			this.form.payment_type = "";
			this.form.bank_account_id = "";
			this.materialRows = [];
			this.closeMaterialDropdown();
			if (this.form.category === "2") {
				this.addMaterialRow();
			}
			if (this.form.category === "3") {
				if (this.form.user_type === "4") {
					this.form.user_type = "";
					this.onUserTypeChange();
				}
				this.syncOtherServiceTotals();
			}
		},

		onProductTypeChange() {
			this.errors = {};
		},

		addMaterialRow() {
			this.materialRows.push({ material_id: "", material_search: "", qty: "", amount: "" });
			this.syncMaterialTotals();
		},

		removeMaterialRow(idx) {
			this.materialRows.splice(idx, 1);
			this.syncMaterialTotals();
		},

		syncMaterialTotals() {
			if (this.form.category !== "2") return;
			const sum = this.materialRows.reduce((t, r) => {
				const a = parseFloat(r.amount);
				if (isNaN(a) || a < 0) return t;
				return t + a;
			}, 0);
			this.form.total_amount = sum ? sum.toFixed(2) : "";
		},

		syncOtherServiceTotals() {
			if (this.form.category !== "3") return;
			const parseCharge = (v) => {
				if (v === "" || v === null || v === undefined) return 0;
				const n = parseFloat(v);
				return isNaN(n) || n < 0 ? 0 : n;
			};
			const sum = parseCharge(this.form.fitment_charge) + parseCharge(this.form.fuel_charge) + parseCharge(this.form.other_charges);
			this.form.total_amount = sum.toFixed(2);
		},

		openMaterialDropdown(idx, evt) {
			if (!this.materialRows[idx]) return;
			if (this.closeMaterialDropdownTimer) {
				clearTimeout(this.closeMaterialDropdownTimer);
				this.closeMaterialDropdownTimer = null;
			}
			const el = evt?.target;
			if (!el || !el.getBoundingClientRect) return;
			const r = el.getBoundingClientRect();
			this.materialDropdown.open = true;
			this.materialDropdown.idx = idx;
			this.materialDropdown.top = Math.round(r.bottom + 2);
			this.materialDropdown.left = Math.round(r.left);
			this.materialDropdown.width = Math.round(r.width);
		},

		scheduleCloseMaterialDropdown() {
			if (this.closeMaterialDropdownTimer) clearTimeout(this.closeMaterialDropdownTimer);
			this.closeMaterialDropdownTimer = setTimeout(() => {
				this.closeMaterialDropdown();
			}, 180);
		},

		closeMaterialDropdown() {
			this.materialDropdown.open = false;
			this.materialDropdown.idx = -1;
		},

		filteredMaterials(search) {
			const q = String(search || "").toLowerCase().trim();
			if (!q) return this.rawMaterials.slice(0, 20);
			return this.rawMaterials
				.filter((m) => {
					const name = String(m.raw_material_name || "").toLowerCase();
					const desc = String(m.description || "").toLowerCase();
					return name.includes(q) || desc.includes(q);
				})
				.slice(0, 20);
		},

		selectMaterial(idx, mat) {
			if (!this.materialRows[idx]) return;
			this.materialRows[idx].material_id = String(mat?.id || "");
			this.materialRows[idx].material_search = mat?.raw_material_name ? String(mat.raw_material_name) : "";
		},

		materialLabelById(id) {
			const m = this.rawMaterials.find((x) => String(x.id) === String(id));
			if (!m) return "#" + String(id);
			return `${m.raw_material_name}`;
		},

		onUserTypeChange() {
			this.errors = {};
			this.selectedDealer = null;
			this.selectedCustomer = null;
			this.isNewDealer = false;
			this.isNewCustomer = false;
			this.dealerSearch = "";
			this.customerSearch = "";
			this.form.user_name = "";
			this.form.user_mobile = "";
			this.form.ship_to_address = "";
			this.clearNewCustomerFields();
			this.clearNewDealerFields();
			this.clearDealerCustomerPaymentFields();
		},

		clearDealerCustomerPaymentFields() {
			this.form.payment_status = "";
			this.form.payment_type = "";
			this.form.cash_online = "";
			this.form.cash_in_hand = "";
			this.form.amount_from_credit = "";
			this.form.bank_account_id = "";
			this.form.payment_id = "";
			this.userCredit = null;
			this.userCreditError = "";
			this.userCreditLoading = false;
		},

		onPaymentStatusChange() {
			if (this.form.payment_status !== "1") {
				this.form.payment_type = "";
				this.form.cash_online = "";
				this.form.cash_in_hand = "";
				this.form.payment_id = "";
				this.form.bank_account_id = "";
			}
			if (this.form.payment_status === "2") {
				this.form.payment_date = "";
				delete this.errors.payment_date;
			}
			if (this.form.payment_status !== "4") {
				this.form.amount_from_credit = "";
			} else if (!String(this.form.amount_from_credit || "").trim()) {
				const t = this.saleTotalAmount;
				if (t > 0) {
					this.form.amount_from_credit = String(t);
				}
			}
		},

		onPaymentTypeChange() {
			if (this.form.payment_type === "2") {
				this.form.cash_in_hand = "";
			} else if (this.form.payment_type === "1") {
				this.form.cash_online = "";
				this.form.payment_id = "";
				this.form.bank_account_id = "";
				delete this.errors.bank_account_id;
			}
		},

		async maybeFetchUserCreditForSale() {
			if (this.form.payment_status !== "4" || !this.salePayerUserId) {
				this.userCredit = null;
				this.userCreditError = "";
				this.userCreditLoading = false;
				return;
			}
			await this.fetchUserCreditForSale();
		},

		async fetchUserCreditForSale() {
			try {
				this.getToken();
				this.userCreditLoading = true;
				this.userCreditError = "";
				const res = await this.$axios.get("/staffs/getUserCredit", {
					params: { user_id: this.salePayerUserId },
					headers: { Authorization: "Bearer " + this.token },
				});
				const ok = res.data?.status === true || res.data?.status === "true";
				if (!ok) {
					this.userCredit = null;
					this.userCreditError = res.data?.message || "Failed to fetch advance balance";
					return;
				}
				this.userCredit = res.data?.data?.user_credit || null;
			} catch (e) {
				this.userCredit = null;
				this.userCreditError = e?.response?.data?.message || "Failed to fetch advance balance";
			} finally {
				this.userCreditLoading = false;
			}
		},

		validate() {
			this.errors = {};
			if (!this.form.category) {
				this.errors.category = "Select category";
				return false;
			}
			if (this.form.category === "1" && !this.form.product_type) {
				this.errors.product_type = "Select product type";
				return false;
			}
			if (!this.form.user_type) {
				this.errors.user_type = "Select user type";
				return false;
			}
			if (this.form.category === "3" && this.form.user_type !== "1" && this.form.user_type !== "2") {
				this.errors.user_type = "Select dealer or customer";
				return false;
			}
			if (this.form.user_type === "4" && !this.form.bank_account_id) {
				this.errors.bank_account_id = "Select bank account";
				return false;
			}

			if (this.form.user_type === "1") {
				if (this.isNewDealer) {
					if (!String(this.form.dealer_name || "").trim()) {
						this.errors.dealer_name = "Enter dealer name";
						return false;
					}
					if (!String(this.form.dealer_mobile || "").trim()) {
						this.errors.dealer_mobile = "Enter dealer mobile";
						return false;
					}
					if (!String(this.form.address || "").trim()) {
						this.errors.address = "Enter address";
						return false;
					}
					if (!this.form.state_id) {
						this.errors.state_id = "Select state";
						return false;
					}
					if (!this.form.district_id) {
						this.errors.district_id = "Select district";
						return false;
					}
					if (!String(this.form.password || "").trim()) {
						this.errors.password = "Password is required";
						return false;
					}
				} else if (!this.dealerAccountUserId(this.selectedDealer)) {
					this.errors.dealer_id = "Select dealer";
					return false;
				}
			}
			if (this.form.user_type === "2") {
				if (this.isNewCustomer) {
					if (!String(this.form.user_name || "").trim()) {
						this.errors.user_name = "Enter customer name";
						return false;
					}
					if (!String(this.form.user_mobile || "").trim()) {
						this.errors.customer_mobile = "Enter customer mobile";
						return false;
					}
					if (!String(this.form.password || "").trim()) {
						this.errors.password = "Password is required";
						return false;
					}
				} else if (!this.selectedCustomer?.user_id) {
					this.errors.customer_id = "Select customer";
					return false;
				}
			}
			if (this.form.user_type === "4") {
				if (!String(this.form.user_name || "").trim()) {
					this.errors.user_name = "Enter customer name";
					return false;
				}
				if (!String(this.form.user_mobile || "").trim()) {
					this.errors.user_mobile = "Enter customer mobile";
					return false;
				}
			}

			if (this.form.category === "1") {
				const q = parseInt(this.form.quantity, 10);
				if (isNaN(q) || q < 0) {
					this.errors.quantity = "Enter a valid quantity";
					return false;
				}
			} else if (this.form.category === "2") {
				if (!this.materialRows.length) {
					this.errors.raw_material_ids = "Add at least one material";
					return false;
				}
				for (const r of this.materialRows) {
					if (!r.material_id) {
						this.errors.raw_material_ids = "Select material for all rows";
						return false;
					}
					const qty = parseInt(r.qty, 10);
					if (isNaN(qty) || qty < 1) {
						this.errors.raw_material_qty = "Enter valid quantity for all rows";
						return false;
					}
					const amt = parseFloat(r.amount);
					if (isNaN(amt) || amt < 0) {
						this.errors.raw_material_price = "Enter valid amount for all rows";
						return false;
					}
				}
			} else if (this.form.category === "3") {
				this.syncOtherServiceTotals();
				const parseCharge = (v) => {
					if (v === "" || v === null || v === undefined) return 0;
					const n = parseFloat(v);
					return n;
				};
				const fitment = parseCharge(this.form.fitment_charge);
				const fuel = parseCharge(this.form.fuel_charge);
				const other = parseCharge(this.form.other_charges);
				if (isNaN(fitment) || fitment < 0) {
					this.errors.fitment_charge = "Enter a valid Fitment charge";
					return false;
				}
				if (isNaN(fuel) || fuel < 0) {
					this.errors.fuel_charge = "Enter a valid Fuel Charge";
					return false;
				}
				if (isNaN(other) || other < 0) {
					this.errors.other_charges = "Enter a valid Other Charges";
					return false;
				}
				if (!String(this.form.remarks || "").trim()) {
					this.errors.remarks = "Enter remarks";
					return false;
				}
			}

			const base = parseFloat(this.form.total_amount);
			if (isNaN(base) || base < 0) {
				this.errors.total_amount = "Enter a valid total amount";
				return false;
			}
			if (this.form.category !== "3") {
				const gstRate = parseFloat(this.form.gst_amount);
				if (isNaN(gstRate) || gstRate < 0) {
					this.errors.gst_amount = "Select a valid GST option";
					return false;
				}
			}
			const saleTotal = this.saleTotalAmount;

			if (this.isDealerOrCustomer && this.showSaleAmountBlock) {
				if (!this.form.payment_status) {
					this.errors.payment_status = "Select payment status";
					return false;
				}
				if (this.form.payment_status === "1") {
					if (!this.form.payment_type) {
						this.errors.payment_type = "Select payment type when payment is received";
						return false;
					}
					const pt = this.form.payment_type;
					if (pt === "2" || pt === "3") {
						if (!this.form.bank_account_id) {
							this.errors.bank_account_id = "Select bank account";
							return false;
						}
						const co = parseFloat(this.form.cash_online);
						if (isNaN(co) || co <= 0) {
							this.errors.cash_online = "Enter a valid online amount";
							return false;
						}
					}
					if (pt === "1" || pt === "3") {
						const ch = parseFloat(this.form.cash_in_hand);
						if (isNaN(ch) || ch <= 0) {
							this.errors.cash_in_hand = "Enter a valid cash in hand amount";
							return false;
						}
					}
				}
				if (this.form.payment_status === "4") {
					const ac = parseFloat(this.form.amount_from_credit);
					if (isNaN(ac) || ac <= 0) {
						this.errors.amount_from_credit = "Enter amount from advance";
						return false;
					}
					if (this.userCredit && this.userCredit.credit_available_amount != null) {
						const avail = parseFloat(this.userCredit.credit_available_amount);
						if (!isNaN(avail) && ac > avail) {
							this.errors.amount_from_credit = "Amount cannot exceed available advance";
							return false;
						}
					}
					if (!isNaN(ac) && ac > saleTotal) {
						this.errors.amount_from_credit = "Amount cannot exceed sale total";
						return false;
					}
				}
			}

			if (this.isPaymentDateRequired && !this.form.payment_date) {
				this.errors.payment_date = "Select payment date";
				return false;
			}

			return true;
		},

		buildPayload() {
			const gstSelected = parseFloat(this.form.gst_amount);
			const userType = parseInt(this.form.user_type, 10);
			const category = parseInt(this.form.category, 10);
			const payload = {
				category,
				product_type: category === 1 ? parseInt(this.form.product_type, 10) : null,
				user_type: userType,
				bank_account_id: null,
				quantity: category === 1 ? parseInt(this.form.quantity, 10) : null,
				gst: isNaN(gstSelected) ? 0 : gstSelected,
				total_amount: parseFloat(this.form.total_amount),
				payment_id: null,
				payment_date: String(this.form.payment_date || "").trim() || null,
				invoice_number: String(this.form.invoice_number || "").trim() || null,
				invoice_date: String(this.form.invoice_date || "").trim() || null,
				remarks: String(this.form.remarks || "").trim() || null,
				user_name: String(this.form.user_name || "").trim() || null,
				user_mobile: String(this.form.user_mobile || "").trim() || null,
				ship_to_address: null,
				dealer_id: null,
				customer_id: null,
				password: null,
				payment_status: null,
				payment_type: null,
				cash_online: null,
				cash_in_hand: null,
				amount_from_credit: null,
				raw_material_ids: null,
				raw_material_qty: null,
				raw_material_price: null,
				fitment_charge: null,
				fuel_charge: null,
				other_charges: null,
			};

			if (category === 2) {
				payload.raw_material_ids = this.materialRows.map((r) => parseInt(r.material_id, 10));
				payload.raw_material_qty = this.materialRows.map((r) => parseInt(r.qty, 10));
				payload.raw_material_price = this.materialRows.map((r) => parseFloat(r.amount));
				payload.quantity = payload.raw_material_qty.reduce((t, q) => t + (isNaN(q) ? 0 : q), 0);
			}

			if (category === 3) {
				this.syncOtherServiceTotals();
				const parseCharge = (v) => {
					if (v === "" || v === null || v === undefined) return 0;
					const n = parseFloat(v);
					return isNaN(n) ? 0 : n;
				};
				payload.fitment_charge = parseCharge(this.form.fitment_charge);
				payload.fuel_charge = parseCharge(this.form.fuel_charge);
				payload.other_charges = parseCharge(this.form.other_charges);
				payload.total_amount = Number((payload.fitment_charge + payload.fuel_charge + payload.other_charges).toFixed(2));
				payload.gst = 0;
				payload.product_type = null;
				payload.quantity = 0;
				payload.payment_status = 2;
				payload.payment_type = null;
				payload.bank_account_id = null;
				payload.payment_id = null;
				payload.payment_date = null;
				payload.invoice_number = null;
				payload.invoice_date = null;
			}

			if (userType === 4 && this.form.bank_account_id) {
				payload.bank_account_id = parseInt(this.form.bank_account_id, 10);
			}

			if (this.isDealerOrCustomer && category !== 3) {
				payload.payment_status = parseInt(this.form.payment_status, 10);
				const ps = this.form.payment_status;
				if (ps === "1") {
					payload.payment_type = parseInt(this.form.payment_type, 10);
					const pt = this.form.payment_type;
					if (pt === "2" || pt === "3") {
						payload.bank_account_id = parseInt(this.form.bank_account_id, 10);
						payload.cash_online = parseFloat(this.form.cash_online);
						payload.payment_id = String(this.form.payment_id || "").trim() || null;
					}
					if (pt === "1" || pt === "3") {
						payload.cash_in_hand = parseFloat(this.form.cash_in_hand);
					}
				} else if (ps === "4") {
					payload.amount_from_credit = parseFloat(this.form.amount_from_credit);
				}
			} else if (category !== 3) {
				const pid = String(this.form.payment_id || "").trim();
				payload.payment_id = pid || null;
			}

			if (this.form.user_type === "1") {
				if (this.isNewDealer) {
					payload.dealer_name = String(this.form.dealer_name || "").trim();
					payload.dealer_mobile = String(this.form.dealer_mobile || "").trim();
					payload.address = String(this.form.address || "").trim();
					payload.state_id = parseInt(this.form.state_id, 10);
					payload.district_id = parseInt(this.form.district_id, 10);
					payload.password = String(this.form.password || "").trim();
				} else {
					payload.dealer_id = this.dealerAccountUserId(this.selectedDealer);
				}
			} else if (this.form.user_type === "2") {
				if (this.isNewCustomer) {
					payload.user_name = String(this.form.user_name || "").trim();
					payload.user_mobile = String(this.form.user_mobile || "").trim();
					payload.password = String(this.form.password || "").trim();
				} else {
					payload.customer_id = parseInt(this.selectedCustomer.user_id, 10);
				}
			}

			if (this.isDealerOrCustomer && category !== 3) {
				payload.ship_to_address = String(this.form.ship_to_address || "").trim() || null;
			}

			return payload;
		},

		resetAll() {
			this.errors = {};
			this.submitting = false;
			this.dealerSearch = "";
			this.customerSearch = "";
			this.selectedDealer = null;
			this.selectedCustomer = null;
			this.isNewDealer = false;
			this.isNewCustomer = false;
			this.showDealerSuggestions = false;
			this.showCustomerSuggestions = false;
			this.districts = [];
			this.userCredit = null;
			this.userCreditError = "";
			this.userCreditLoading = false;
			this.materialRows = [];
			this.closeMaterialDropdown();
			this.form = {
				category: "",
				product_type: "",
				user_type: "",
				bank_account_id: "",
				payment_status: "",
				payment_type: "",
				cash_online: "",
				cash_in_hand: "",
				amount_from_credit: "",
				quantity: "",
				gst_amount: "0",
				total_amount: "",
				fitment_charge: "0",
				fuel_charge: "0",
				other_charges: "0",
				payment_id: "",
				payment_date: "",
				invoice_number: "",
				invoice_date: "",
				remarks: "",
				user_name: "",
				user_mobile: "",
				dealer_name: "",
				dealer_mobile: "",
				address: "",
				state_id: "",
				district_id: "",
				password: "123456",
				ship_to_address: "",
			};
		},

		async submit() {
			if (this.submitting) return;
			if (this.isDealerOrCustomer && this.showSaleAmountBlock && this.form.payment_status === "4") {
				await this.maybeFetchUserCreditForSale();
			}
			if (!this.validate()) return;
			this.submitting = true;
			this.getToken();
			try {
				const payload = this.buildPayload();
				const res = await this.$axios.post("/staffs/createSale", payload, {
					headers: { Authorization: "Bearer " + this.token, "Content-Type": "application/json" },
				});
				const ok = res.data?.status === true || res.data?.status === "true";
				if (ok) {
					this.$swal("Success", this.$extractApiMessage(res.data?.message) || "Sale created", "success");
					this.resetAll();
					this.fetchSaleList();
					this.fetchAllProducts();
					this.fetchRawMaterials();
					this.fetchBankAccounts();
					this.fetchDealers();
					this.fetchCustomers();
				} else {
					this.$applyApiFieldErrors(this.errors, res.data);
					this.$swal("Error", this.$getApiErrorMessage(res.data, "Failed to create sale"), "error");
				}
			} catch (err) {
				this.$applyApiFieldErrors(this.errors, err);
				this.$swal("Error", this.$getApiErrorMessage(err, "Something went wrong"), "error");
			} finally {
				this.submitting = false;
			}
		},
	},
};
</script>
